import React from 'react';

import {
    DiscoveryUnit,
    DiscoveryUnitItem,
    PAGE_TYPE_SUBS_LOGGED_IN_HOMEPAGE,
} from '@udemy/discovery-api';
import {DeviceType} from '@udemy/hooks';
import {
    LectureStackRecommendationUnit,
    LectureStackRecommendationUnitSkeleton,
} from '@udemy/react-browse-lecture';
import {DiscoveryUnitsContainer} from '@udemy/react-discovery-units';

import {UserOccupationInfo} from 'src/lib/fetchers';

interface LecturesDiscoveryUnitProps {
    userOccupationInfo?: UserOccupationInfo;
    deviceType: DeviceType;
    className?: string;
}

export const LecturesDiscoveryUnit: React.FC<LecturesDiscoveryUnitProps> = ({
    userOccupationInfo,
    deviceType,
    className = 'component-margin',
}) => {
    // Guard for direct usage — the parent already checks hasOccupationGroupName before rendering.
    if (
        !userOccupationInfo?.group?.name &&
        !userOccupationInfo?.canonical?.id &&
        !userOccupationInfo?.raw?.id
    ) {
        return null;
    }

    // Prefer the canonical occupation's representative topic name, then the group name.
    // Mirrors the monolith: occupationData.representativeTopicName ?? occupationData.occupationGroupName
    const representativeTopicName =
        userOccupationInfo.canonical?.representativeTopic?.name ??
        userOccupationInfo.group?.name ??
        userOccupationInfo.canonical?.name ??
        userOccupationInfo.raw?.name;

    // Pass occupation_id when available so the discovery API can narrow lectures to the user's
    // exact occupation. Without it the API falls back to session-level occupation data, which is
    // the same behaviour as the monolith when occupationData.occupationId is undefined.
    // Note: Django's isApprovedOccupation and the GraphQL canonical field can diverge — a user
    // may have isApprovedOccupation=true in Django while canonical=null in GraphQL. Using raw.id
    // as a secondary fallback mirrors Django passing occupationId for non-canonical occupations.
    const occupationId =
        userOccupationInfo.canonical?.id ?? userOccupationInfo.raw?.id ?? undefined;
    const pageObject = occupationId ? {occupation_id: occupationId} : undefined;

    const getUnitComponentAndProps = (
        unit: Partial<DiscoveryUnit<DiscoveryUnitItem>> | undefined,
    ) => {
        if (!unit) throw new Error('Missing unit');

        return {
            component: LectureStackRecommendationUnit,
            componentProps: {
                ...unit,
                representativeTopicName,
                isStandaloneUnit: true,
            },
        };
    };

    const skeletonComponent = ({className: skeletonClassName}: {className?: string}) => (
        <LectureStackRecommendationUnitSkeleton
            representativeTopicName={representativeTopicName ?? ''}
            className={skeletonClassName}
            isStandaloneUnit={true}
        />
    );

    return (
        <div className={className} data-purpose="lectures-on-landing-container">
            <DiscoveryUnitsContainer
                pageType={PAGE_TYPE_SUBS_LOGGED_IN_HOMEPAGE}
                pageObject={pageObject}
                deviceType={deviceType}
                getUnitComponentAndProps={getUnitComponentAndProps}
                skeletonComponent={skeletonComponent}
                filter={(u) => {
                    return u.view_type === 'lectures_by_topic';
                }}
                disableInfiniteScroll={true}
                fetchOptions={{pageSize: 3}}
            />
        </div>
    );
};
